<template>
<form @submit.prevent="handleSubmit()">
  <b-loading :active="loading" :is-full-page="false" />

  <template v-if="!loading">
    <cytomine-modal :active="active" :title="modalTitle" @close="$emit('update:active', false)">
      <!-- Add/Rename mode -->
      <template v-if="mode === 'add' || mode === 'rename'">
        <b-field 
          :label="$t('name')" 
          :type="{ 'is-danger': errors.has('name') || duplicateError }" 
          :message="duplicateError || errors.first('name')"
        >
          <b-input 
            v-model="name" 
            name="name" 
            v-validate="'required|trim_max_50'"
            @input="checkDuplicateName"
          />
        </b-field>
      </template>

      <!-- Delete mode -->
      <template v-if="mode === 'delete'">
        <b-message type="is-danger" has-icon icon-size="is-small" >
          <span v-html="$t('delete-worklist-confirmation-message', {worklistName: worklist ? worklist.name : ''})"></span>
        </b-message>
      </template>

      <template #footer>
        <button class="button" type="button" @click="$emit('update:active', false)">
          {{ $t('button-cancel') }}
        </button>
        <button 
          v-if="mode === 'delete'"
          class="button is-danger" 
          type="submit"
          :disabled="loading"
        >
          {{ $t('button-confirm') }}
        </button>
        <button 
          v-else
          class="button is-link" 
          type="submit"
          :disabled="errors.any() || duplicateError || loading"
        >
          {{ $t('button-save') }}
        </button>
      </template>
    </cytomine-modal>
  </template>
</form>
</template>

<script>
import {Worklist, WorklistCollection} from 'cytomine-client';
import { normalizeName, normalizeNameForCompare } from '@/utils/string-utils';
import CytomineModal from '@/components/utils/CytomineModal';
import {get} from '@/utils/store-helpers';
import { Validator } from 'vee-validate';

export default {
  name: 'add-worklist-modal',
  props: {
    active: Boolean,
    mode: {
      type: String,
      default: 'add',
      validator: (value) => ['add', 'rename', 'delete'].includes(value)
    },
    worklist: {
      type: Object,
      default: null
    }
  },
  components: {CytomineModal},
  $_veeValidate: {validator: 'new'},
  data() {
    return {
      loading: false,
      name: '',
      existingWorklists: [],
      duplicateError: null,
    };
  },
  computed: {
    normalizedName() {
      return normalizeName(this.name);
    },
    currentUser: get('currentUser/user'),
    modalTitle() {
      switch(this.mode) {
        case 'add':
          return this.$t('create-worklist');
        case 'rename':
          return this.$t('rename-worklist');
        case 'delete':
          return this.$t('delete-worklist');
        default:
          return this.$t('create-worklist');
      }
    }
  },
  watch: {
    async active(val) {
      if(val) {
        // Reset state
        this.duplicateError = null;
        this.loading = false;
        
        // Load existing worklists for duplicate check
        if(this.mode === 'add' || this.mode === 'rename') {
          try {
            let data = await WorklistCollection.fetchAll({
              filterKey: 'user', 
              filterValue: this.currentUser.id
            });
            this.existingWorklists = data.array || [];
          }
          catch(error) {
            console.error('Failed to load worklists:', error);
            this.existingWorklists = [];
          }
        }

        // Set initial name based on mode
        if(this.mode === 'add') {
          this.name = '';
        }
        else if(this.mode === 'rename' && this.worklist) {
          this.name = this.worklist.name;
        }
      }
    }
  },
  methods: {
    checkDuplicateName() {
      this.duplicateError = null;

      const normalizedName = normalizeName(this.name);
      if(!normalizedName) {
        return;
      }

      if (normalizedName.length > 50) {
        this.duplicateError = this.$t('error-worklist-name-max-length');
        return;
      }

      const isDuplicate = this.existingWorklists.some(wl => {
        if(this.mode === 'rename' && this.worklist && wl.id === this.worklist.id) {
          return false;
        }

        return normalizeNameForCompare(wl.name) === normalizeNameForCompare(this.name);
      });

      if(isDuplicate) {
        this.duplicateError = this.$t('notif-error-worklist-already-exists');
      }
    },

    async handleSubmit() {
      if(this.mode === 'add') {
        await this.createWorklist();
      }
      else if(this.mode === 'rename') {
        await this.renameWorklist();
      }
      else if(this.mode === 'delete') {
        await this.deleteWorklist();
      }
    },

    async createWorklist() {
      let result = await this.$validator.validateAll();

      const name = this.normalizedName;

      if (!result || this.duplicateError) {
        return;
      }

      if (name.length > 50) {
        this.duplicateError = this.$t('error-worklist-name-max-length');
        return;
      }

      this.loading = true;

      try {
        let worklist = await new Worklist({name}).save();
        
        this.$notify({type: 'success', text: this.$t('notif-success-worklist-creation')});
        this.$emit('update:active', false);
        
        // Event bus를 통해 워크리스트 갱신 이벤트 발생
        this.$eventBus.$emit('refreshWorklist');
        
        this.$router.push({ path: `/worklist/id-${worklist.id}` });
      }
      catch(error) {
        console.error('Error creating worklist:', error);
        if(error.response && error.response.status === 409) {
          this.$notify({type: 'error', text: this.$t('notif-error-worklist-already-exists')});
        }
        else {
          this.$notify({type: 'error', text: this.$t('notif-error-worklist-creation')});
        }
      }
      finally {
        this.loading = false;
      }
    },

    async renameWorklist() {
      let result = await this.$validator.validateAll();
      if(!result || this.duplicateError) {
        return;
      }

      if(!this.worklist) {
        console.error('No worklist provided for rename');
        return;
      }

      this.loading = true;

      try {
        let updatedWorklist = new Worklist(this.worklist);
        updatedWorklist.name = normalizeName(this.name);
        await updatedWorklist.save();

        this.$notify({
          type: 'success', 
          text: this.$t('notif-success-worklist-rename', {worklistName: this.name})
        });
        this.$emit('update:active', false);
        this.$emit('renamed', updatedWorklist);
        
        // Event bus를 통해 워크리스트 갱신 이벤트 발생
        this.$eventBus.$emit('refreshWorklist');
      }
      catch(error) {
        console.error('Error renaming worklist:', error);
        this.$notify({
          type: 'error', 
          text: this.$t('notif-error-worklist-rename', {worklistName: this.worklist.name})
        });
      }
      finally {
        this.loading = false;
      }
    },

    async deleteWorklist() {
      if(!this.worklist) {
        console.error('No worklist provided for deletion');
        return;
      }

      this.loading = true;

      try {
        let worklistModel = new Worklist(this.worklist);
        await worklistModel.delete();

        this.$notify({
          type: 'success',
          text: this.$t('notif-success-worklist-deletion')
        });
        
        this.$emit('update:active', false);
        this.$emit('deleted', this.worklist.id);
        
        // Event bus를 통해 워크리스트 갱신 이벤트 발생
        this.$eventBus.$emit('refreshWorklist');
        
        // 삭제된 페이지는 히스토리에서 제거하고 status-1로 이동
        if (this.$route.path !== '/worklist/status-1') { // avoid redundant navigation
          this.$router.replace({ path: '/worklist/status-1' });
        }
      }
      catch(error) {
        console.error('Error deleting worklist:', error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-worklist-deletion')
        });
      }
      finally {
        this.loading = false;
      }
    }
  },
  async created() {
    Validator.extend('trim_max_50', {
      getMessage: () => this.$t('error-worklist-name-max-length'), 
      validate: value => {
        if (!value) return true;
        return value.trim().length <= 50;
      }
    });
  }
};
</script>
